import { useMemo } from 'react';
import { useOfflineModeManager } from 'FrontRoyalAngular';
import { modifyHref } from 'NavigationHelpers';
import { lessonsApi, type ExternalLinkMetadatum, type StreamForStreamDashboard } from 'Lessons';
import { isExternalLinkWorking, sortExternalLinks } from '../selectors';
import { useStreamRequestOptions } from './useStreamRequestOptions';

const { useGetExternalLinksForStreamQuery } = lessonsApi;

/*
    Drops broken links, sorts required links first, then by sort. modifyHref rewrites the old
    uploads.smart.ly host. No request goes out while offline.
*/
export function useExternalLinksForStream(stream: StreamForStreamDashboard | null) {
    const offlineModeManager = useOfflineModeManager();
    const { userCohortIds } = useStreamRequestOptions();

    const { data } = useGetExternalLinksForStreamQuery(
        { streamId: stream?.id ?? '', userCohortIds },
        { skip: !stream || offlineModeManager.inOfflineMode },
    );

    return useMemo(() => {
        const links = data?.contents.externalLinkMetadata ?? [];
        const usable = sortExternalLinks(links.filter(isExternalLinkWorking)).map(link => ({
            ...link,
            url: modifyHref(link.url),
        }));

        const byLessonId = usable.reduce<Record<string, ExternalLinkMetadatum[]>>((acc, link) => {
            acc[link.lessonId] = [...(acc[link.lessonId] ?? []), link];
            return acc;
        }, {});

        return { links: usable, linksByLessonId: byLessonId };
    }, [data]);
}
